import { Formik, FormikHelpers } from 'formik'
import { ReactElement, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import Modal from 'components/v2/Modal'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import { ILocationDragEndForm } from 'features/RoutingProjects/types'
import { RoutingProjectDow } from 'gql/generated/graphql'
import { schema } from './schema'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { ApolloError } from '@apollo/client'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { getUnprocessableErrors } from 'utils/error'
import { SelectDow } from 'features/RoutingProjects/components/SelectDow'

interface ILocationDragEndModalProps {
  onClose: () => void
  availableDows: RoutingProjectDow[]
  onSubmit: (dows: RoutingProjectDow[]) => Promise<void>
  onSuccess: () => void
}

export const LocationDragEndModal = ({
  onClose,
  onSubmit,
  onSuccess,
  availableDows
}: ILocationDragEndModalProps): ReactElement => {
  const intl = useIntl()
  const [error, setError] = useState<ApolloError | null>(null)
  const initialValues: ILocationDragEndForm = {
    dows: availableDows
  }

  const handleSubmit = async (
    values: ILocationDragEndForm,
    { setSubmitting }: FormikHelpers<ILocationDragEndForm>
  ): Promise<void> => {
    try {
      await onSubmit(values.dows)
      onSuccess()
    } catch (error) {
      setError(error as ApolloError)
    }
    setSubmitting(false)
  }

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage
            id="routingProject.updateOptimization.location.dragEnd.title"
            defaultMessage="Apply change on"
          />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(props): ReactElement => {
          const { setFieldValue, handleBlur, touched, errors, values, isSubmitting } = props

          const handleDowsChange = (dows: RoutingProjectDow[]): void => {
            setFieldValue('dows', dows)
          }

          return (
            <form onSubmit={props.handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <SelectDow
                      id="name"
                      values={values.dows}
                      onChange={handleDowsChange}
                      onBlur={handleBlur}
                      touched={touched.dows}
                      errors={errors.dows}
                      label={<FormattedMessage id="routingProjects.locationDragEnd.dows.label" defaultMessage="DOW" />}
                      placeholder={intl.formatMessage({
                        id: 'routingProjects.locationDragEnd.dows.placeholder',
                        defaultMessage: 'Select DOW'
                      })}
                      availableDows={availableDows}
                    />
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="routingProjects.locationDragEnd.error"
                        defaultMessage="There was an error applying the changes"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button kind="cancel" onClick={onClose}>
                    <FormattedMessage id="modal.actions.close" defaultMessage="Close" />
                  </Button>
                  <Button kind="primary" loading={isSubmitting}>
                    <FormattedMessage id="routingProjects.create.submit" defaultMessage="Save" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
